import { describeApiError, type Room } from '@brush/api-client';
import { ErrorNotice, Ratio, Spinner, cohortCard } from '@brush/ui';
import { Users } from 'lucide-react';
import type { ReactNode } from 'react';
import {
  activePlayersLine,
  connectionCard,
  dormantCard,
  featureLines,
  internalNotice,
  newReturningLine,
} from '../activity/overview';
import { useRoomActivity } from '../queries';

/**
 * The operator Overview's activity cards (ADR 0012 Decision 16, step 20): the room's own players — its
 * value and its growth — never compared with another room, and counts only. Every rule deciding what a
 * card says is in `activity/overview.ts`; this only lays the words out. No signal orange anywhere: none of
 * this is a job to do.
 */
export function RoomActivitySection({ room }: { room: Room }) {
  const query = useRoomActivity(room.id);

  return (
    <section className="mt-6 overflow-hidden rounded-xl border border-ink-800/10 bg-white shadow-sm">
      <header className="flex items-center gap-2 border-b border-ink-800/10 px-6 py-4">
        <Users className="size-4 text-gold-500" />
        <h2 className="font-display font-semibold text-ink-950">Players</h2>
      </header>
      <div className="p-6">
        {query.isPending ? (
          <Spinner />
        ) : query.isError ? (
          <ErrorNotice message={describeApiError(query.error)} />
        ) : (
          <ActivityCards room={room} activity={query.data} />
        )}
      </div>
    </section>
  );
}

function ActivityCards({ room, activity }: { room: Room; activity: NonNullable<ReturnType<typeof useRoomActivity>['data']> }) {
  const notice = internalNotice(activity);
  const active = activePlayersLine(activity);
  const split = newReturningLine(activity);
  const connection = connectionCard(activity);
  const dormant = dormantCard(activity);
  const features = featureLines(activity, room);
  const cohort = cohortCard(activity.cohort ?? null);

  return (
    <div className="space-y-6">
      {notice ? <p className="text-sm text-ink-800">{notice}</p> : null}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Tile label="Active players" now={active.now} last={active.last} />
        <Tile label="New and returning" now={split.now} last={split.last} />
        <Tile
          label="Connected cards"
          now={<Ratio numerator={connection.claimed} denominator={connection.cards} />}
          last={connection.last}
          hint="Cards with a player's account connected, out of the room's cards."
        />
        <Tile label="Dormant players" now={dormant.now} last={dormant.last} hint={dormant.definition} />
      </div>

      {features.length > 0 ? (
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-800">In the player portal</div>
          <ul className="mt-2 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {features.map((f) => (
              <li key={f.feature} className="rounded-lg border border-ink-800/10 p-3">
                <div className="text-xs text-ink-800/70">{f.title}</div>
                <div className="mt-1 text-sm text-ink-950">{f.now}</div>
                <div className="text-xs text-ink-800/60">{f.last}</div>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-[11px] text-ink-800/60">Each player counts once per feature, however often they came back.</p>
        </div>
      ) : null}

      <div>
        <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-800">Players coming back</div>
        {cohort.kind === 'not-measured' ? (
          <p className="mt-2 text-xs text-ink-800">{cohort.says}</p>
        ) : (
          <>
            <div className="mt-2 overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-ink-800/60">
                    <th className="py-1 pr-4 font-medium">First seen</th>
                    {cohort.months.map((m) => (
                      <th key={m} className="py-1 pr-4 font-medium">
                        Active in {m}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink-800/10">
                  {cohort.rows.map((row) => (
                    <tr key={row.label}>
                      <td className="py-1.5 pr-4 text-ink-950">{row.label}</td>
                      {row.cells.map((cell, i) => (
                        <td key={cohort.months[i]} className="min-w-[8rem] py-1.5 pr-4">
                          {cell ? <Ratio numerator={cell.players} denominator={cell.size} /> : null}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-2 text-[11px] text-ink-800/60">
              A player belongs to the month this room first saw them. Up to six months, ending with the last one
              the room has closed.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function Tile({ label, now, last, hint }: { label: string; now: ReactNode; last: string; hint?: string }) {
  return (
    <div className="rounded-lg border border-ink-800/10 p-4">
      <div className="text-xs text-ink-800/70">{label}</div>
      <div className="mt-1 text-sm text-ink-950">{now}</div>
      <div className="mt-0.5 text-xs text-ink-800/60">{last}</div>
      {hint ? <p className="mt-2 text-[11px] text-ink-800/60">{hint}</p> : null}
    </div>
  );
}
